// Unified account profile page. Reads the signed-in profile from the auth
// store (populated from /api/me in backend mode or the ID token in direct mode).

import { useAuth } from "../store/auth.js";

function displayName(profile: { name?: string; givenName?: string; familyName?: string; email?: string } | null): string {
  if (!profile) return "Signed in";
  if (profile.name) return profile.name;
  const parts = [profile.givenName, profile.familyName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (profile.email) return profile.email;
  return "Signed in";
}

export function ProfilePage(): JSX.Element {
  const { ready, session, profile, login } = useAuth();

  if (!ready) {
    return (
      <main className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center text-slate-400">
        loading…
      </main>
    );
  }

  if (!session) {
    return (
      <main className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center px-6">
        <div className="w-full max-w-sm text-center space-y-4">
          <h1 className="text-2xl font-semibold">Sign in to view your profile</h1>
          <button
            onClick={() => void login("/profile")}
            className="w-full rounded-xl bg-white text-slate-900 font-semibold py-3 hover:bg-slate-200 transition"
          >
            Sign in with Google
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="px-6 py-12">
      <div className="mx-auto w-full max-w-md space-y-6">
        <header>
          <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
          <p className="text-slate-400">Your account information.</p>
        </header>

        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6">
          <div className="flex items-center gap-4">
            {profile?.picture ? (
              <img
                src={profile.picture}
                alt=""
                className="h-16 w-16 rounded-full border border-slate-700"
              />
            ) : (
              <div className="h-16 w-16 rounded-full bg-slate-800 border border-slate-700" />
            )}
            <div className="min-w-0">
              <div className="text-lg font-semibold truncate">{displayName(profile)}</div>
              {profile?.email && <div className="text-sm text-slate-400 truncate">{profile.email}</div>}
            </div>
          </div>

          <dl className="mt-6 space-y-3 text-sm">
            {profile?.givenName && <Row label="First name" value={profile.givenName} />}
            {profile?.familyName && <Row label="Last name" value={profile.familyName} />}
            {profile?.sub && <Row label="User ID" value={profile.sub} />}
          </dl>
        </div>
      </div>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-800 pb-2 last:border-0 last:pb-0">
      <dt className="text-slate-400">{label}</dt>
      <dd className="text-right text-slate-200 break-all">{value}</dd>
    </div>
  );
}
